/**
 * ============================================================================
 * PERSONALIZED DISCOVERY SET — 5-Fragrance Validator
 * ============================================================================
 *
 * Purpose: keep the Add to Cart button locked AND show a prominent helper
 * message until the customer has actively selected all 5 fragrances on the
 * Personalized Discovery Set PDP.
 *
 * Supports BOTH product setups:
 *   - Active product (Shark Apps):  properties[Fragrance One] through Five
 *   - Draft/Original product:       items[0]id through items[4]id
 *
 * The script auto-detects which pattern is on the page and applies the same
 * validation logic to either.
 *
 * Why this is needed: without the lock, customers who skip dropdowns end up
 * with malformed bundle orders (e.g. order DTC353384 had only 4 fragrances).
 * The visual cue (greyed button + warning message) also makes it obvious to
 * the customer that they need to finish picking before checking out.
 *
 * Robustness notes:
 *   - Styles are injected with !important so theme CSS can't override them.
 *   - The button gets BOTH a class and inline styles, and the disabled
 *     attribute is enforced on a 500ms interval so other theme scripts
 *     can't silently re-enable it before all 5 are picked.
 *   - The helper message is styled as a visible warning panel, not faint
 *     italic text, so it's hard to miss.
 * ============================================================================
 */

(function () {
  'use strict';

  const DROPDOWN_NAMES_PATTERN_A = [
    'properties[Fragrance One]',
    'properties[Fragrance Two]',
    'properties[Fragrance Three]',
    'properties[Fragrance Four]',
    'properties[Fragrance Five]'
  ];

  const DROPDOWN_NAMES_PATTERN_B = [
    'items[0]id',
    'items[1]id',
    'items[2]id',
    'items[3]id',
    'items[4]id'
  ];

  const MAX_WAIT_MS = 10000;
  const POLL_INTERVAL_MS = 200;
  const REVALIDATE_INTERVAL_MS = 500;
  const LOCKED_CLASS = 'discovery-set-locked';
  const HELPER_CLASS = 'discovery-set-helper';

  let elapsed = 0;
  let initialized = false;

  function injectStylesOnce() {
    if (document.getElementById('discovery-set-validator-styles')) return;
    const style = document.createElement('style');
    style.id = 'discovery-set-validator-styles';
    style.textContent = [
      '.' + HELPER_CLASS + ' {',
      '  display: block;',
      '  margin: 14px 0 10px;',
      '  padding: 12px 16px;',
      '  background: #fff8e1;',
      '  border: 2px solid #c79c1a;',
      '  border-radius: 4px;',
      '  color: #5d4313;',
      '  font-size: 15px;',
      '  font-weight: 600;',
      '  line-height: 1.4;',
      '  text-align: center;',
      '  text-transform: none;',
      '  letter-spacing: 0.02em;',
      '  font-style: normal;',
      '}',
      '.' + HELPER_CLASS + '.' + HELPER_CLASS + '--hidden { display: none !important; }',
      'button.' + LOCKED_CLASS + ',',
      'button.' + LOCKED_CLASS + ':hover,',
      'button.' + LOCKED_CLASS + ':focus,',
      'button.' + LOCKED_CLASS + ':active {',
      '  opacity: 0.45 !important;',
      '  filter: grayscale(1) !important;',
      '  cursor: not-allowed !important;',
      '  pointer-events: none !important;',
      '  background-color: #999 !important;',
      '  color: #fff !important;',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  function getBundleForm() {
    // Sentinel: the validator only runs when the bundle form is present.
    // This prevents the broader ATC selector from matching unrelated
    // submit buttons (e.g. the cart page's checkout button) on pages
    // where dropdowns happen to match the bundle pattern.
    return document.querySelector('[data-product-bundle]');
  }

  function findDropdowns(scope) {
    const root = scope || document;
    let dropdowns = [];
    DROPDOWN_NAMES_PATTERN_A.forEach(function (name) {
      const el = root.querySelector('select[name="' + name + '"]');
      if (el) dropdowns.push(el);
    });
    if (dropdowns.length === 5) return dropdowns;

    dropdowns = [];
    DROPDOWN_NAMES_PATTERN_B.forEach(function (name) {
      const el = root.querySelector('select[name="' + name + '"]');
      if (el) dropdowns.push(el);
    });
    return dropdowns;
  }

  function findAtcButton(scope) {
    // Search ONLY inside the bundle form so we never accidentally
    // target the cart page's checkout button (class cart__checkout
    // matches button[type="submit"][class*="cart"]).
    const root = scope || document;
    return root.querySelector(
      '#AddToCart, [data-add-to-cart], [name="add"], button[type="submit"][class*="cart"]'
    );
  }

  function waitForDropdowns() {
    if (initialized) return;

    // Hard scope: bail if we aren't on a bundle PDP. The bundle form is
    // the only place the dropdown selectors and the ATC button should
    // both exist; without it we risk attaching the validator UI to an
    // unrelated submit button (e.g. the cart page's checkout button).
    const bundleForm = getBundleForm();
    if (!bundleForm) return;

    const dropdowns = findDropdowns(bundleForm);

    if (dropdowns.length === 5) {
      initialized = true;
      init(dropdowns, bundleForm);
      return;
    }

    elapsed += POLL_INTERVAL_MS;
    if (elapsed >= MAX_WAIT_MS) {
      console.warn('[DISCOVERY-SET] Timed out waiting for 5 dropdowns. Found: ' + dropdowns.length);
      return;
    }

    setTimeout(waitForDropdowns, POLL_INTERVAL_MS);
  }

  function init(dropdowns, bundleForm) {
    console.log('[DISCOVERY-SET] Validator loaded — found all 5 fragrance dropdowns');

    injectStylesOnce();

    const atcButton = findAtcButton(bundleForm);
    if (!atcButton) {
      console.warn('[DISCOVERY-SET] Could not find Add to Cart button. Validator not running.');
      return;
    }

    // Reset any auto-selected dropdowns to the empty "Choose Option" state.
    dropdowns.forEach(function (dropdown) {
      const emptyOption =
        dropdown.querySelector('option[disabled][value=""]') ||
        dropdown.querySelector('option[value=""]');
      if (emptyOption) {
        dropdown.value = '';
        emptyOption.selected = true;
      }
    });

    // Helper message goes immediately above the button.
    const helperMsg = document.createElement('p');
    helperMsg.className = HELPER_CLASS;
    helperMsg.setAttribute('role', 'status');
    helperMsg.setAttribute('aria-live', 'polite');
    atcButton.parentNode.insertBefore(helperMsg, atcButton);

    function countSelected() {
      let n = 0;
      for (let i = 0; i < dropdowns.length; i++) {
        const v = dropdowns[i].value;
        if (v && v.trim() !== '') n++;
      }
      return n;
    }

    function setLocked(locked, remaining) {
      if (locked) {
        atcButton.disabled = true;
        atcButton.classList.add(LOCKED_CLASS);
        atcButton.setAttribute('aria-disabled', 'true');
        helperMsg.classList.remove(HELPER_CLASS + '--hidden');
        if (remaining === 5) {
          helperMsg.textContent = 'Please select 5 fragrances to continue';
        } else if (remaining === 1) {
          helperMsg.textContent = 'Please select 1 more fragrance to continue';
        } else {
          helperMsg.textContent = 'Please select ' + remaining + ' more fragrances to continue';
        }
      } else {
        atcButton.disabled = false;
        atcButton.classList.remove(LOCKED_CLASS);
        atcButton.removeAttribute('aria-disabled');
        helperMsg.classList.add(HELPER_CLASS + '--hidden');
      }
    }

    function validateSelections() {
      const selectedCount = countSelected();
      const remaining = 5 - selectedCount;
      setLocked(remaining > 0, remaining);
    }

    // Re-run on any dropdown change OR input event (in case of programmatic value set)
    dropdowns.forEach(function (dropdown) {
      dropdown.addEventListener('change', validateSelections);
      dropdown.addEventListener('input', validateSelections);
    });

    // Re-validate on a short interval so any other theme/app script that
    // tries to silently re-enable the button gets undone within 500ms.
    setInterval(validateSelections, REVALIDATE_INTERVAL_MS);

    // Final safety net: block submit if somehow 5 aren't selected at submit time.
    const form = atcButton.closest('form');
    if (form) {
      form.addEventListener(
        'submit',
        function (e) {
          if (countSelected() < 5) {
            e.preventDefault();
            e.stopPropagation();
            try { alert('Please select all 5 fragrances before adding to cart.'); } catch (err) { /* ignore */ }
          }
        },
        true
      );
    }

    validateSelections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForDropdowns);
  } else {
    waitForDropdowns();
  }
})();
